import { ReactElement, useState } from 'react';
import { endOfWeek, format, isValid, startOfWeek } from 'date-fns';
import FormControl, { FormControlProps } from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Popover from '@material-ui/core/Popover';
import DateFnsUtils from '@date-io/date-fns';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';

import { ReactComponent as CalendarIcon } from '~/ui/assets/images/calendarIcon.svg';

import variables from '~/ui/assets/styles/colors.module.scss';
import styles from './DatePicker.module.scss';

export type WeekRange = [Date, Date];

interface IProps {
  size?: FormControlProps['size'];
  label?: string;
  error?: string;
  value: WeekRange | null;
  onChange: (range: WeekRange) => void;
  /** Override displayed value (e.g. "Nov - Dec - Jan") */
  displayValue?: string;
  disabled?: boolean;
  className?: string;
  tabIndex?: number;
  showIcon?: boolean;
  fullWidth?: boolean;
  /** Week starts on: 0 = Sunday, 1 = Monday */
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
}

const formatWeekRange = (range: WeekRange | null): string => {
  if (!range || !isValid(range[0]) || !isValid(range[1])) return '';
  const sameYear = range[0].getFullYear() === range[1].getFullYear();
  const startStr = format(range[0], sameYear ? 'd MMM' : 'd MMM yyyy');
  const endStr = format(range[1], 'd MMM yyyy');
  return `${startStr} - ${endStr}`;
};

const WeekPickerMUI = ({
  size,
  label,
  error,
  value,
  onChange,
  displayValue,
  disabled,
  className,
  tabIndex = 0,
  showIcon = true,
  fullWidth = true,
  weekStartsOn = 0,
}: IProps): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const isError = Boolean(error);

  const displayText = displayValue ?? formatWeekRange(value);
  const pickerDate = value?.[0] ?? new Date();

  const handleOpen = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled) setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    if (anchorEl instanceof HTMLElement) {
      anchorEl.querySelector<HTMLInputElement>('input')?.blur();
    }
    setAnchorEl(null);
  };

  const handleDateChange = (date: Date | null) => {
    if (!date || !isValid(date)) return;
    const start = startOfWeek(date, { weekStartsOn });
    const end = endOfWeek(date, { weekStartsOn });
    onChange([start, end]);
    handleClose();
  };

  return (
    <FormControl
      variant="outlined"
      size={size}
      fullWidth={fullWidth}
      className={styles.datePickerControl}
    >
      {label && (
        <InputLabel shrink error={isError}>
          {label}
        </InputLabel>
      )}
      <OutlinedInput
        value={displayText}
        readOnly
        onClick={handleOpen}
        error={!!error}
        disabled={disabled}
        tabIndex={tabIndex}
        className={className}
        endAdornment={showIcon ? <CalendarIcon color={variables.colorRed} /> : undefined}
        label={label}
        notched={!!label}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        TransitionProps={{ timeout: 0 }}
        disableRestoreFocus
        PaperProps={{ className: styles.weekPickerPopover }}
      >
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <DatePicker variant="static" value={pickerDate} onChange={handleDateChange} autoOk />
        </MuiPickersUtilsProvider>
      </Popover>

      {error && (
        <FormHelperText error className={styles.errorText}>
          {error.toString()}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default WeekPickerMUI;
